import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Clock, Layers, Sparkles, ShieldCheck } from "lucide-react";
import { Reveal, MaskedLines } from "../components/Reveal";
import Marquee from "../components/Marquee";
import SEO from "../components/SEO";

const IMG_HERO = "https://images.unsplash.com/photo-1725758262622-8e4454bd350c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600&h=900&fit=crop";
const IMG_WOOD = "https://images.pexels.com/photos/9699343/pexels-photo-9699343.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1000";
const IMG_METAL = "https://i.etsystatic.com/23382479/r/il/40a7f5/4646291351/il_1080xN.4646291351_gwt8.jpg";

function ParallaxImg({ src, className }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  return (
    <div ref={ref} className={`overflow-hidden border border-line ${className}`}>
      <motion.img style={{ y, scale: 1.2 }} src={src} alt="" className="h-full w-full object-cover" />
    </div>
  );
}

const MATERIALS = [
  {
    icon: Layers,
    title: "Le bois",
    text: "Hêtre, chêne, bambou, contreplaqué. Le laser brûle la surface avec une précision au dixième de millimètre pour révéler un contraste chaleureux, sans encre ni vernis.",
  },
  {
    icon: Sparkles,
    title: "Le métal",
    text: "Acier inoxydable, laiton, aluminium anodisé. La gravure marque la matière en profondeur : un rendu net et haut de gamme qui ne s'efface jamais.",
  },
];

const STEPS = [
  { n: "01", title: "Votre motif", text: "Vous nous envoyez votre texte, prénom ou logo. On le prépare (vectorisation) pour une gravure nette." },
  { n: "02", title: "Le calage", text: "Puissance, vitesse et focale sont réglées à la main sur une chute avant de lancer la pièce finale." },
  { n: "03", title: "La gravure", text: "Le laser marque la matière en profondeur, en monochrome : une empreinte permanente qui ne s'efface pas." },
  { n: "04", title: "La finition", text: "On nettoie la pièce et, pour le bois, on huile la surface pour révéler le contraste de la gravure." },
];

export default function GravureLaser() {
  return (
    <div>
      <SEO title="Gravure laser sur bois et métal" path="/gravure-laser" image={IMG_HERO}
        description="Gravure laser en Suisse : une marque permanente dans le bois et le métal, sans encre. Prénom, logo, date — chaque pièce est calée et contrôlée à la main." />
      <section className="mx-auto max-w-[1400px] px-5 pt-24 pb-16 md:px-10">
        <p className="mb-4 text-xs uppercase tracking-[0.3em] text-terra">Savoir-faire</p>
        <h1 className="max-w-4xl font-serif text-5xl font-light leading-[0.95] tracking-tight text-ink md:text-8xl">
          <MaskedLines lines={["La gravure", "laser."]} />
        </h1>
        <Reveal delay={0.3}>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-ink2">
            Une marque permanente, gravée directement dans le <strong className="text-ink">bois</strong> et le{" "}
            <strong className="text-ink">métal</strong>. Pas d&apos;encre, pas de couche qui pèle : la matière elle-même
            porte votre message. Idéal pour un cadeau qui dure, une signalétique ou une pièce unique.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 pb-24 md:px-10">
        <ParallaxImg src={IMG_HERO} className="aspect-[16/9] w-full" />
      </section>

      {/* Matières */}
      <section className="mx-auto max-w-[1400px] px-5 pb-8 md:px-10">
        <Reveal>
          <p className="mb-4 text-xs uppercase tracking-[0.3em] text-terra">Les matières</p>
          <h2 className="max-w-2xl font-serif text-4xl font-light leading-tight tracking-tight text-ink md:text-6xl">
            Deux matières, un rendu durable.
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {MATERIALS.map((m, i) => (
            <Reveal key={m.title} delay={i * 0.1} className="border border-line bg-sand/40 p-8 md:p-10">
              <m.icon className="h-8 w-8 text-terra" strokeWidth={1.5} />
              <h3 className="mt-6 font-serif text-2xl font-medium text-ink md:text-3xl">{m.title}</h3>
              <p className="mt-3 leading-relaxed text-ink2">{m.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto grid max-w-[1400px] gap-4 px-5 py-24 md:grid-cols-12 md:px-10">
        <ParallaxImg src={IMG_WOOD} className="aspect-[4/5] md:col-span-6" />
        <ParallaxImg src={IMG_METAL} className="aspect-[4/5] md:col-span-6" />
      </section>

      <Marquee items={["Gravure Laser", "Bois & Métal", "Marque Permanente", "Pièce Unique", "Fait Maison"]} />

      {/* Comment ça marche */}
      <section className="mx-auto max-w-[1400px] px-5 py-24 md:px-10">
        <Reveal>
          <p className="mb-4 text-xs uppercase tracking-[0.3em] text-terra">Comment ça marche</p>
          <h2 className="max-w-2xl font-serif text-4xl font-light leading-tight tracking-tight text-ink md:text-6xl">
            De l&apos;idée à la pièce gravée.
          </h2>
        </Reveal>
        <div className="mt-12 space-y-6">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.06} className="flex items-start gap-5 border-t border-line pt-6 md:gap-8">
              <span className="w-12 shrink-0 font-serif text-3xl font-light text-terra md:text-4xl">{s.n}</span>
              <div>
                <h3 className="font-serif text-xl font-medium text-ink md:text-2xl">{s.title}</h3>
                <p className="mt-1 max-w-2xl leading-relaxed text-ink2">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Délais */}
      <section className="mx-auto max-w-[1400px] px-5 pb-24 md:px-10">
        <Reveal className="flex flex-col gap-8 border border-line bg-sand/40 p-8 md:flex-row md:items-center md:justify-between md:p-12">
          <div className="flex items-start gap-4">
            <Clock className="mt-1 h-7 w-7 shrink-0 text-terra" strokeWidth={1.5} />
            <div>
              <h3 className="font-serif text-2xl font-medium text-ink">Délais indicatifs</h3>
              <p className="mt-2 max-w-xl leading-relaxed text-ink2">
                Comptez <strong className="text-ink">3 à 7 jours ouvrés</strong> pour une pièce gravée sur-mesure,
                selon la complexité et la matière. Les produits déjà réalisés partent sous 48h.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <ShieldCheck className="mt-1 h-7 w-7 shrink-0 text-terra" strokeWidth={1.5} />
            <div>
              <h3 className="font-serif text-2xl font-medium text-ink">Garantie fait maison</h3>
              <p className="mt-2 max-w-xs leading-relaxed text-ink2">
                Chaque pièce est contrôlée à la main avant l&apos;envoi.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-[1400px] px-5 pb-32 md:px-10">
        <Reveal className="border border-line bg-ink p-10 text-cream md:p-20">
          <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
            <h2 className="max-w-2xl font-serif text-4xl font-light leading-tight tracking-tight md:text-6xl">
              Voir nos pièces gravées.
            </h2>
            <Link
              to="/produits"
              data-testid="gravure-cta"
              className="group flex shrink-0 items-center gap-2 border border-cream px-8 py-4 text-sm uppercase tracking-widest transition-colors hover:bg-cream hover:text-ink active:scale-95"
            >
              Découvrir les produits
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
